/**
 * Client-side image normalization: re-encode large user uploads to WebP and
 * scale the long edge to `maxDim` so we never store 8000 px originals.
 *
 * Falls back to the original file when:
 *  - the file is not a raster image we can decode (e.g. SVG, GIF, HEIC)
 *  - the browser cannot encode WebP via canvas.toBlob
 *  - the conversion would produce a *larger* file than the input
 */

const PASSTHROUGH_TYPES = new Set(["image/webp", "image/gif", "image/svg+xml"]);

/**
 * Central upload encoding settings. ALL upload paths (apply, account profile,
 * community submissions, admin uploads) MUST use these so every stored image
 * is produced with identical WebP quality and maximum dimensions.
 */
export const UPLOAD_MAX_DIM = 2400;
/** WebP encoder quality (0–1) used for every upload. */
export const UPLOAD_WEBP_QUALITY = 0.82;

/**
 * Maximum raw file size we accept from the user before conversion (25 MB).
 * Normal raster images are re-encoded to a small WebP before upload, so this
 * generous limit only affects the original picked from the device.
 */
export const UPLOAD_MAX_INPUT_BYTES = 25 * 1024 * 1024;

/**
 * Supabase's standard `.upload()` rejects files larger than ~6 MB. Raster
 * images are converted to a small WebP first, so they pass easily. Formats we
 * cannot convert (SVG, GIF, HEIC) keep their original size — guard against
 * those exceeding the storage limit with a clear, user-facing error.
 */
const UPLOAD_MAX_STORED_BYTES = 6 * 1024 * 1024;

export function assertUploadable(file: File): void {
  if (file.size > UPLOAD_MAX_STORED_BYTES) {
    throw new Error(
      "Datei ist nach der Optimierung zu groß – bitte als JPG oder PNG hochladen.",
    );
  }
}

/**
 * Common *static* image formats we accept for upload. Animated/scriptable
 * formats (GIF, SVG) and APNG are intentionally excluded.
 */
export const STATIC_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
  "image/avif",
  "image/tiff",
  "image/bmp",
] as const;

/** HTML `accept` attribute value matching {@link STATIC_IMAGE_TYPES}. */
export const STATIC_IMAGE_ACCEPT = STATIC_IMAGE_TYPES.join(",");

/**
 * True when the file is a common static image. Falls back to the file
 * extension when the browser reports an empty/odd MIME type (e.g. HEIC on
 * some devices).
 */
export function isStaticImage(file: File): boolean {
  const type = (file.type || "").toLowerCase();
  if (type) {
    if (type === "image/gif" || type === "image/svg+xml" || type === "image/apng") return false;
    return (STATIC_IMAGE_TYPES as readonly string[]).includes(type);
  }
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  return ["jpg", "jpeg", "png", "webp", "heic", "heif", "avif", "tif", "tiff", "bmp"].includes(ext);
}

export type ToWebpOptions = {
  maxDim?: number;
  quality?: number;
};

/**
 * Fetch an already-uploaded image URL back into a `File` so it can be
 * re-processed (e.g. rotated) and re-uploaded. Public Supabase storage URLs
 * support CORS GET requests.
 */
export async function urlToFile(url: string): Promise<File> {
  const res = await fetch(url, { mode: "cors" });
  if (!res.ok) throw new Error("Bild konnte nicht geladen werden.");
  const blob = await res.blob();
  const name = url.split("/").pop()?.split("?")[0] || "image.webp";
  return new File([blob], name, { type: blob.type || "image/webp" });
}

/**
 * Rotate an image file clockwise by 90/180/270 degrees and return a new File.
 * The result is NOT size-optimized — run it through {@link toWebP} before
 * uploading.
 */
export async function rotateImage(file: File, degrees: 90 | 180 | 270 = 90): Promise<File> {
  if (typeof window === "undefined") return file;
  const bitmap = await loadBitmap(file);
  const sw = bitmap.width;
  const sh = bitmap.height;
  const swap = degrees === 90 || degrees === 270;
  const canvas = document.createElement("canvas");
  canvas.width = swap ? sh : sw;
  canvas.height = swap ? sw : sh;
  const ctx = canvas.getContext("2d");
  if (!ctx) return file;
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((degrees * Math.PI) / 180);
  ctx.drawImage(bitmap, -sw / 2, -sh / 2);
  if ("close" in bitmap && typeof (bitmap as ImageBitmap).close === "function") {
    (bitmap as ImageBitmap).close();
  }
  const blob: Blob | null = await new Promise((resolve) =>
    canvas.toBlob(resolve, "image/webp", UPLOAD_WEBP_QUALITY),
  );
  if (!blob) return file;
  const baseName = file.name.replace(/\.[^.]+$/, "") || "image";
  return new File([blob], `${baseName}.webp`, { type: "image/webp", lastModified: Date.now() });
}

export async function toWebP(file: File, opts: ToWebpOptions = {}): Promise<File> {
  const { maxDim = UPLOAD_MAX_DIM, quality = UPLOAD_WEBP_QUALITY } = opts;
  if (typeof window === "undefined") return file;
  if (!file.type.startsWith("image/")) return file;
  if (PASSTHROUGH_TYPES.has(file.type)) return file;

  try {
    const bitmap = await loadBitmap(file);
    const { width: sw, height: sh } = bitmap;
    const baseName = file.name.replace(/\.[^.]+$/, "");
    // Encode, progressively shrinking dimensions and quality until the output
    // is comfortably below the storage limit. This guarantees even very large
    // phone photos (e.g. 48 MP) end up as a small WebP/JPEG instead of being
    // rejected for being "too large after optimization".
    let curDim = maxDim;
    let curQuality = quality;
    let best: { blob: Blob; type: string } | null = null;

    for (let attempt = 0; attempt < 6; attempt++) {
      const scale = Math.min(1, curDim / Math.max(sw, sh));
      const tw = Math.max(1, Math.round(sw * scale));
      const th = Math.max(1, Math.round(sh * scale));

      const canvas = document.createElement("canvas");
      canvas.width = tw;
      canvas.height = th;
      const ctx = canvas.getContext("2d");
      if (!ctx) break;
      ctx.drawImage(bitmap, 0, 0, tw, th);

      let blob: Blob | null = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/webp", curQuality),
      );
      let outType = "image/webp";
      // Some browsers can't encode WebP via canvas — fall back to JPEG.
      if (!blob || blob.type !== "image/webp") {
        blob = await new Promise((resolve) =>
          canvas.toBlob(resolve, "image/jpeg", curQuality),
        );
        outType = "image/jpeg";
      }
      if (blob && (blob.type === "image/webp" || blob.type === "image/jpeg")) {
        best = { blob, type: blob.type };
        outType = blob.type;
        if (blob.size <= UPLOAD_MAX_STORED_BYTES * 0.9) break;
      }
      // Still too big (or encode failed): shrink and lower quality, retry.
      curDim = Math.round(curDim * 0.8);
      curQuality = Math.max(0.5, curQuality - 0.1);
    }

    if ("close" in bitmap && typeof (bitmap as ImageBitmap).close === "function") {
      (bitmap as ImageBitmap).close();
    }

    if (!best) return file;
    const ext = best.type === "image/jpeg" ? "jpg" : "webp";
    // Never return something larger than the original raster file.
    if (best.blob.size >= file.size && Math.max(sw, sh) <= maxDim) return file;
    return new File([best.blob], `${baseName}.${ext}`, { type: best.type, lastModified: Date.now() });
  } catch {
    return file;
  }
}

/**
 * Returns a storage-safe content type for an upload. Prefers the file's own
 * image/* MIME type, derives one from the extension as a fallback, and finally
 * falls back to a generic binary type so the upload never stores an empty
 * Content-Type header.
 */
export function safeContentType(file: File): string {
  if (file.type && file.type.startsWith("image/")) return file.type;
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  switch (ext) {
    case "webp":
      return "image/webp";
    case "png":
      return "image/png";
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "gif":
      return "image/gif";
    case "svg":
      return "image/svg+xml";
    case "avif":
      return "image/avif";
    default:
      return "application/octet-stream";
  }
}

async function loadBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file);
    } catch {
      // fall through to <img> path
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = "async";
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error("decode-failed"));
      img.src = url;
    });
    return img;
  } finally {
    // The HTMLImageElement keeps its own reference; safe to revoke after decode.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}